import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import CityAutocomplete from '../common/CityAutocomplete';
import UserAvatar from '../common/UserAvatar';
import TimeDatePickerModal from '../common/TimeDatePickerModal';
import axios from 'axios';
import { reauthenticateWithPopup, reauthenticateWithCredential, GoogleAuthProvider, EmailAuthProvider, deleteUser, sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink, updateProfile } from 'firebase/auth';
import { auth, storage } from '../../firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import './Profile.css';
import FreelancerProfile from '../features/FreelancerProfile';

// Determine API base URL
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (!envUrl) {
    return process.env.NODE_ENV === 'production' ? '' : 'https://flask-router-340481947694.europe-west1.run.app';
  }
  if (envUrl.includes('your-cloud-run-url') || envUrl.includes('placeholder')) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  if (envUrl.includes('run.app')) {
    return envUrl;
  }
  if (process.env.NODE_ENV === 'production') {
    return envUrl;
  }
  return 'http://localhost:8081';
};

const API_BASE_URL = getApiBaseUrl();

export default function Profile() {
  const { currentUser, currentRole, userRoles, switchRole, logout } = useAuth();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [profile, setProfile] = useState({ visible: false });
  const [loading, setLoading] = useState(true);
  const [pendingDeletion, setPendingDeletion] = useState(false);
  const [updatingVisibility, setUpdatingVisibility] = useState(false);
  const [editingBusinessType, setEditingBusinessType] = useState(false);
  const [businessTypeValue, setBusinessTypeValue] = useState('');
  const [editingDisplayName, setEditingDisplayName] = useState(false);
  const [displayNameValue, setDisplayNameValue] = useState('');
  const [editingLocation, setEditingLocation] = useState(false);
  const [locationValue, setLocationValue] = useState('');
  const [locationValid, setLocationValid] = useState(false);
  const [editingAbout, setEditingAbout] = useState(false);
  const [aboutValue, setAboutValue] = useState('');
  const [showVisibilityForm, setShowVisibilityForm] = useState(false);
  const [employeesNeeded, setEmployeesNeeded] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [jobStartDate, setJobStartDate] = useState('');
  const [jobStartTime, setJobStartTime] = useState('');
  const [jobEndDate, setJobEndDate] = useState('');
  const [jobEndTime, setJobEndTime] = useState('');
  const [jobLocation, setJobLocation] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showAvailabilityForm, setShowAvailabilityForm] = useState(false);
  const [availabilityRanges, setAvailabilityRanges] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  const fileInputRefFreelancer = useRef(null);
  const [showAvailabilityPicker, setShowAvailabilityPicker] = useState(false);
  const [editingRangeIndex, setEditingRangeIndex] = useState(null);
  const [showChangePasswordForm, setShowChangePasswordForm] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState('');
  const [showPasswordRecoveryForm, setShowPasswordRecoveryForm] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [recoveryNewPassword, setRecoveryNewPassword] = useState('');
  const [recoveryConfirmPassword, setRecoveryConfirmPassword] = useState('');
  const [requestingRecoveryCode, setRequestingRecoveryCode] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [recoveryError, setRecoveryError] = useState('');
  const [recoveryCodeSent, setRecoveryCodeSent] = useState(false);

  // Helper function to format time input to HH:MM (24-hour format)
  const formatTimeInput = (value) => {
    // Remove any non-digit characters
    const digits = value.replace(/\D/g, '');

    // Limit to 4 digits
    if (digits.length > 4) return value;

    // Format as HH:MM
    if (digits.length <= 2) {
      return digits;
    } else {
      return digits.slice(0, 2) + ':' + digits.slice(2, 4);
    }
  };

  // Helper function to validate time format (HH:MM, 24-hour)
  const validateTime = (time) => {
    if (!time) return false;
    const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(time);
  };

  const addAvailabilityRange = (range) => {
    if (editingRangeIndex !== null) {
      setAvailabilityRanges((prev) => {
        const updated = [...prev];
        updated[editingRangeIndex] = range;
        return updated;
      });
      setEditingRangeIndex(null);
    } else {
      setAvailabilityRanges((prev) => [...prev, range]);
    }
    setShowAvailabilityPicker(false);
  };

  const removeAvailabilityRange = (index) => {
    setAvailabilityRanges((prev) => prev.filter((_, i) => i !== index));
  };

  const editAvailabilityRange = (index) => {
    setEditingRangeIndex(index);
    setShowAvailabilityPicker(true);
  };

  const fetchProfile = useCallback(async () => {
    if (!currentUser) return;

    try {
      const token = await currentUser.getIdToken();
      let response;
      try {
        response = await axios.get(`${API_BASE_URL}/api/user/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          timeout: 5000
        });
      } catch (userError) {
        response = await axios.get(`${API_BASE_URL}/api/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          timeout: 5000
        });
      }
      const profileData = response.data || {};
      if (!profileData.hasOwnProperty('visible')) {
        profileData.visible = false;
      }
      setProfile(profileData);
    } catch (error) {
      // Error fetching profile
      setProfile({ visible: false });
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!profile && !loading && currentUser) {
      setProfile({ visible: false });
    }
  }, [profile, loading, currentUser]);

  // Check for email link re-authentication when component mounts or URL changes
  useEffect(() => {
    const checkEmailLinkReauth = async () => {
      if (!currentUser) return;
      // Check if there's a pending deletion and an email link in the URL
      const isPending = window.localStorage.getItem('pendingDeletion') === 'true';
      if (isPending && isSignInWithEmailLink(auth, window.location.href)) {
        // User clicked the re-authentication link
        setDeletingAccount(true);
        setPendingDeletion(true);
        try {
          const emailForSignIn = window.localStorage.getItem('emailForSignIn') || currentUser.email;
          if (!emailForSignIn) {
            throw new Error('Email is required for re-authentication');
          }

          // Create credential from the link (don't sign in, just create credential)
          const credential = EmailAuthProvider.credentialWithLink(emailForSignIn, window.location.href);

          // Re-authenticate with the credential
          const reauthResult = await reauthenticateWithCredential(currentUser, credential);

          // Clear the URL and localStorage
          window.history.replaceState({}, document.title, window.location.pathname);
          window.localStorage.removeItem('emailForSignIn');
          window.localStorage.removeItem('pendingDeletion');

          // Now proceed with deletion
          await completeAccountDeletion(reauthResult.user);
        } catch (reauthError) {
          console.error('Email link re-authentication error:', reauthError);
          setDeletingAccount(false);
          setPendingDeletion(false);
          window.localStorage.removeItem('pendingDeletion');
          window.localStorage.removeItem('emailForSignIn');

          if (reauthError.code === 'auth/invalid-action-code' || reauthError.code === 'auth/expired-action-code') {
            alert(t('profile.reauthLinkExpired') || 'The re-authentication link has expired. Please try deleting your account again.');
          } else {
            alert(t('profile.reauthFailed') || 'Re-authentication failed. Please try again.');
          }
        }
      }
    };

    checkEmailLinkReauth();
  }, [currentUser, t]);

  // Separate function to complete account deletion after re-authentication
  const completeAccountDeletion = async (reauthenticatedUser) => {
    try {
      // Get fresh auth token after re-authentication
      const token = await reauthenticatedUser.getIdToken(true);

      // Call backend API to delete all user data
      const deleteResponse = await fetch(`${API_BASE_URL}/api/user/account`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!deleteResponse.ok) {
        let errorMessage = 'Failed to delete account';
        try {
          const errorData = await deleteResponse.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (parseError) {
          errorMessage = deleteResponse.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // Delete Firebase Auth user (now that we're re-authenticated)
      const userToDelete = auth.currentUser || reauthenticatedUser;
      if (userToDelete) {
        await deleteUser(userToDelete);
      }

      // Navigate to homepage
      navigate('/');
    } catch (error) {
      console.error('Error completing account deletion:', error);
      let errorMessage = error.message || 'Unknown error';
      alert(t('login.failedToDeleteAccount') + (errorMessage ? ': ' + errorMessage : ''));
      setDeletingAccount(false);
      setPendingDeletion(false);
    }
  };

  useEffect(() => {
    if (profile?.businessType !== undefined) {
      setBusinessTypeValue(profile.businessType || '');
    }
  }, [profile?.businessType]);

  useEffect(() => {
    if (profile && currentUser) {
      const fallbackName =
        profile.displayName ||
        currentUser.displayName ||
        (currentUser.email && currentUser.email.split('@')[0]) ||
        '';
      setDisplayNameValue(fallbackName);
    }
  }, [profile, currentUser]);

  useEffect(() => {
    if (profile?.location !== undefined) {
      setLocationValue(profile.location || '');
    }
  }, [profile?.location]);

  useEffect(() => {
    if (profile?.about !== undefined) {
      setAboutValue(profile.about || '');
    }
  }, [profile?.about]);

  useEffect(() => {
    if (profile?.employeesNeeded !== undefined) {
      setEmployeesNeeded(profile.employeesNeeded?.toString() || '');
    }
    if (profile?.jobDescription !== undefined) {
      setJobDescription(profile.jobDescription || '');
    }
    if (profile?.jobStartDate !== undefined) {
      setJobStartDate(profile.jobStartDate || '');
    }
    if (profile?.jobStartTime !== undefined) {
      setJobStartTime(profile.jobStartTime || '');
    }
    if (profile?.jobEndDate !== undefined) {
      setJobEndDate(profile.jobEndDate || '');
    }
    if (profile?.jobEndTime !== undefined) {
      setJobEndTime(profile.jobEndTime || '');
    }
    if (profile?.jobLocation !== undefined) {
      setJobLocation(profile.jobLocation || '');
    }
    if (profile?.availabilityRanges !== undefined) {
      setAvailabilityRanges(profile.availabilityRanges || []);
    } else if (profile?.availability !== undefined) {
      // Old weekly format - convert to empty array for now
      setAvailabilityRanges([]);
    }
  }, [profile?.employeesNeeded, profile?.jobDescription, profile?.jobStartDate, profile?.jobStartTime, profile?.jobEndDate, profile?.jobEndTime, profile?.jobLocation, profile?.availability]);

  // Reset end date if start date changes and end date is before new start date
  useEffect(() => {
    if (jobStartDate && jobEndDate && jobEndDate < jobStartDate) {
      setJobEndDate('');
      setJobEndTime('');
    }
  }, [jobStartDate]);

  const updateVisibility = useCallback(async (visible, employeesCount = null, jobDesc = null, jobStartDateValue = null, jobStartTimeValue = null, jobEndDateValue = null, jobEndTimeValue = null, jobLocationValue = null, availabilityData = null) => {
    if (!currentUser || updatingVisibility) return;

    setUpdatingVisibility(true);
    try {
      const token = await currentUser.getIdToken(true);

      if (!token) {
        throw new Error('Failed to get authentication token');
      }

      const profileData = {
        visible: visible,
        email: currentUser.email
      };

      if (visible && currentRole === 'business') {
        if (employeesCount !== null) {
          profileData.employeesNeeded = parseInt(employeesCount) || 0;
        }
        if (jobDesc !== null) {
          profileData.jobDescription = jobDesc.trim();
        }
        if (jobStartDateValue !== null) {
          profileData.jobStartDate = jobStartDateValue;
        }
        if (jobStartTimeValue !== null) {
          profileData.jobStartTime = jobStartTimeValue;
        }
        if (jobEndDateValue !== null) {
          profileData.jobEndDate = jobEndDateValue;
        }
        if (jobEndTimeValue !== null) {
          profileData.jobEndTime = jobEndTimeValue;
        }
        if (jobLocationValue !== null) {
          profileData.jobLocation = jobLocationValue;
        }
      } else if (visible && currentRole !== 'business') {
        // For freelancers, include availability data
        if (availabilityData !== null) {
          profileData.availabilityRanges = availabilityData;
        }
      } else if (!visible) {
        profileData.employeesNeeded = null;
        profileData.jobDescription = null;
        profileData.jobStartDate = null;
        profileData.jobStartTime = null;
        profileData.jobEndDate = null;
        profileData.jobEndTime = null;
        profileData.jobLocation = null;
        profileData.availabilityRanges = null;
      }

      await axios.post(`${API_BASE_URL}/api/user/profile`, profileData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 5000
      });

      setProfile(prev => ({
        ...prev,
        visible: visible,
        email: currentUser.email,
        employeesNeeded: visible && currentRole === 'business' ? (parseInt(employeesCount) || 0) : null,
        jobDescription: visible && currentRole === 'business' ? (jobDesc?.trim() || null) : null,
        jobStartDate: visible && currentRole === 'business' ? (jobStartDateValue || null) : null,
        jobStartTime: visible && currentRole === 'business' ? (jobStartTimeValue || null) : null,
        jobEndDate: visible && currentRole === 'business' ? (jobEndDateValue || null) : null,
        jobEndTime: visible && currentRole === 'business' ? (jobEndTimeValue || null) : null,
        jobLocation: visible && currentRole === 'business' ? (jobLocationValue || null) : null,
        availabilityRanges: visible && currentRole !== 'business' ? (availabilityData || null) : null
      }));

      if (visible) {
        setShowVisibilityForm(false);
        setShowAvailabilityForm(false);
      }
    } catch (error) {
      // Error updating visibility
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || t('dashboard.failedToUpdateVisibility');
      alert(`${t('dashboard.failedToUpdateVisibility')}: ${errorMessage}`);
    } finally {
      setUpdatingVisibility(false);
    }
  }, [currentUser, updatingVisibility, currentRole, t]);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    fetchProfile();
  }, [currentUser, fetchProfile, navigate]);


  const handleDeleteAccount = useCallback(async () => {
    if (!currentUser) {
      return;
    }

    // Confirm deletion
    if (!window.confirm(t('login.sureDeleteAccount'))) {
      return;
    }

    setDeletingAccount(true);

    try {
      // Re-authenticate user before deletion (required by Firebase)
      // Check which provider the user used to sign in
      const providerId = currentUser.providerData && currentUser.providerData.length > 0
        ? currentUser.providerData[0].providerId
        : null;

      let reauthenticatedUser = currentUser;

      if (providerId === 'password') {
        // Check if there's a pending deletion (we already sent the link)
        const isPending = window.localStorage.getItem('pendingDeletion') === 'true';
        if (isPending) {
          // We already sent the link, just wait for user to click it
          alert(t('profile.reauthLinkSent') || 'A re-authentication link has been sent to your email. Please check your email and click the link to complete account deletion.');
          setDeletingAccount(false);
          return;
        }

        // For email/password users, try email link re-authentication first
        // (works for both email link and password users)
        try {
          const actionCodeSettings = {
            url: `${window.location.origin}${window.location.pathname}`,
            handleCodeInApp: true,
          };

          // Store pending deletion
          window.localStorage.setItem('pendingDeletion', 'true');
          window.localStorage.setItem('emailForSignIn', currentUser.email);

          await sendSignInLinkToEmail(auth, currentUser.email, actionCodeSettings);
          alert(t('profile.reauthLinkSent') || 'A re-authentication link has been sent to your email. Please check your email and click the link to complete account deletion.');
          setDeletingAccount(false);
          setPendingDeletion(true);
          return;
        } catch (linkError) {
          console.error('Error sending re-authentication link:', linkError);
          // If sending link fails, try password prompt as fallback (for users who might have password)
          const password = prompt(t('profile.enterPasswordToDelete') || 'Please enter your password to confirm account deletion:');
          if (!password) {
            setDeletingAccount(false);
            window.localStorage.removeItem('pendingDeletion');
            return;
          }

          try {
            // Re-authenticate with email/password
            const credential = EmailAuthProvider.credential(currentUser.email, password);
            const reauthResult = await reauthenticateWithCredential(currentUser, credential);
            reauthenticatedUser = reauthResult.user;
            window.localStorage.removeItem('pendingDeletion');
          } catch (reauthError) {
            if (reauthError.code === 'auth/wrong-password' || reauthError.code === 'auth/invalid-credential') {
              alert(t('profile.wrongPassword') || 'Incorrect password. Please try again.');
            } else {
              alert(t('profile.reauthFailed') || 'Re-authentication failed. Please try again.');
            }
            setDeletingAccount(false);
            window.localStorage.removeItem('pendingDeletion');
            return;
          }
        }
      } else if (providerId === 'google.com') {
        // Google user - use popup
        const provider = new GoogleAuthProvider();
        let reauthResult;
        try {
          reauthResult = await reauthenticateWithPopup(currentUser, provider);
          reauthenticatedUser = reauthResult.user;
        } catch (reauthError) {
          // If popup is blocked, try redirect method as fallback
          if (reauthError.code === 'auth/popup-blocked' || reauthError.code === 'auth/popup-closed-by-user') {
            if (reauthError.code === 'auth/popup-blocked') {
              alert('Please allow popups for this site to delete your account, then try again.');
            }
            setDeletingAccount(false);
            return;
          }
          // For other errors, re-throw
          throw reauthError;
        }
      } else {
        // Unknown provider - try to proceed without re-auth (may fail)
        console.warn('Unknown provider, attempting deletion without re-authentication');
      }

      // Complete deletion using the helper function
      await completeAccountDeletion(reauthenticatedUser);
    } catch (error) {
      // Error deleting account
      // If user cancels re-authentication, don't show error
      if (error.code === 'auth/popup-closed-by-user' ||
          error.code === 'auth/cancelled-popup-request' ||
          error.code === 'auth/popup-blocked') {
        setDeletingAccount(false);
        if (error.code === 'auth/popup-blocked') {
          alert('Please allow popups for this site to delete your account, then try again.');
        }
        return;
      }
      // Handle internal errors with more helpful message
      let errorMessage = error.message || 'Unknown error';
      if (error.code === 'auth/internal-error') {
        errorMessage = 'An internal error occurred. Please try again or contact support if the problem persists.';
      }
      alert(t('login.failedToDeleteAccount') + (errorMessage ? ': ' + errorMessage : ''));
      setDeletingAccount(false);
    }
  }, [currentUser, navigate, t]);

  const handleChangePassword = useCallback(async () => {
    if (!currentUser) {
      return;
    }

    // Validate inputs
    if (!oldPassword.trim()) {
      setChangePasswordError(t('profile.oldPasswordRequired') || 'Old password is required');
      return;
    }

    if (!newPassword.trim()) {
      setChangePasswordError(t('login.passwordRequired') || 'New password is required');
      return;
    }

    if (newPassword.length < 6) {
      setChangePasswordError(t('login.passwordTooShort') || 'New password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setChangePasswordError(t('login.passwordsDoNotMatch') || 'Passwords do not match');
      return;
    }

    setChangePasswordError('');
    setChangingPassword(true);

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          oldPassword: oldPassword.trim(),
          newPassword: newPassword.trim()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('profile.failedToChangePassword') || 'Failed to change password');
      }

      alert(t('profile.passwordChangedSuccess') || 'Password changed successfully!');
      setShowChangePasswordForm(false);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      setChangePasswordError(error.message || t('profile.failedToChangePassword') || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  }, [currentUser, oldPassword, newPassword, confirmPassword, t]);

  const handleRequestPasswordRecoveryCode = useCallback(async () => {
    if (!recoveryEmail.trim()) {
      setRecoveryError(t('login.emailRequired') || 'Email is required');
      return;
    }

    setRecoveryError('');
    setRequestingRecoveryCode(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/request-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: recoveryEmail.trim().toLowerCase()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('profile.failedToRequestRecoveryCode') || 'Failed to request recovery code');
      }

      setRecoveryCodeSent(true);
      setRecoveryError(t('profile.recoveryCodeSent') || 'Password reset code has been sent to your email.');
    } catch (error) {
      console.error('Error requesting recovery code:', error);
      setRecoveryError(error.message || t('profile.failedToRequestRecoveryCode') || 'Failed to request recovery code');
    } finally {
      setRequestingRecoveryCode(false);
    }
  }, [recoveryEmail, t]);

  const handleResetPassword = useCallback(async () => {
    // Validate inputs
    if (!recoveryEmail.trim()) {
      setRecoveryError(t('login.emailRequired') || 'Email is required');
      return;
    }

    if (!recoveryCode.trim() || recoveryCode.length !== 6) {
      setRecoveryError(t('profile.invalidRecoveryCode') || 'Please enter a valid 6-digit code');
      return;
    }

    if (!recoveryNewPassword.trim()) {
      setRecoveryError(t('login.passwordRequired') || 'New password is required');
      return;
    }

    if (recoveryNewPassword.length < 6) {
      setRecoveryError(t('login.passwordTooShort') || 'New password must be at least 6 characters');
      return;
    }

    if (recoveryNewPassword !== recoveryConfirmPassword) {
      setRecoveryError(t('login.passwordsDoNotMatch') || 'Passwords do not match');
      return;
    }

    setRecoveryError('');
    setResettingPassword(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: recoveryEmail.trim().toLowerCase(),
          code: recoveryCode.trim(),
          newPassword: recoveryNewPassword.trim()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('profile.failedToResetPassword') || 'Failed to reset password');
      }

      alert(t('profile.passwordResetSuccess') || 'Password reset successfully! You can now sign in with your new password.');
      setShowPasswordRecoveryForm(false);
      setRecoveryEmail('');
      setRecoveryCode('');
      setRecoveryNewPassword('');
      setRecoveryConfirmPassword('');
      setRecoveryCodeSent(false);
    } catch (error) {
      console.error('Error resetting password:', error);
      setRecoveryError(error.message || t('profile.failedToResetPassword') || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  }, [recoveryEmail, recoveryCode, recoveryNewPassword, recoveryConfirmPassword, t]);

  const handleRoleSwitch = useCallback(async () => {
    if (!currentRole || !userRoles || userRoles.length < 2) {
      alert('You must have both business and freelancer profiles to switch roles.');
      return;
    }

    // Determine the other role
    const newRole = currentRole === 'business' ? 'freelancer' : 'business';

    try {
      await switchRole(newRole);
      // Navigate to the appropriate landing page based on the new active role
      if (newRole === 'business') {
        navigate('/jobs');
      } else {
        navigate('/freelancer/dashboard');
      }
    } catch (error) {
      console.error('Error switching role:', error);
      alert(error.message || 'Failed to switch role. Please try again.');
    }
  }, [switchRole, navigate, currentRole, userRoles]);

  const handlePhotoUpload = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file || !currentUser) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert(t('profile.invalidImageType') || 'Please select an image file.');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert(t('profile.imageTooLarge') || 'Image size must be less than 5MB.');
      return;
    }

    setUploadingPhoto(true);
    try {
      // Delete old photo if exists
      // Try to delete with common extensions
      if (currentUser.photoURL) {
        const commonExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
        for (const ext of commonExtensions) {
          try {
            const oldPhotoRef = ref(storage, `profile-photos/${currentUser.uid}.${ext}`);
            await deleteObject(oldPhotoRef);
            break; // If deletion succeeds, stop trying
          } catch (deleteError) {
            // Continue trying other extensions
            continue;
          }
        }
        // Also try without extension (for backwards compatibility)
        try {
          const oldPhotoRef = ref(storage, `profile-photos/${currentUser.uid}`);
          await deleteObject(oldPhotoRef);
        } catch (deleteError) {
          // Ignore errors when deleting old photo (might not exist)
          console.log('No old photo to delete or error deleting:', deleteError);
        }
      }

      // Upload new photo with metadata
      // Get file extension or default to jpg
      const fileExtension = file.name.split('.').pop() || 'jpg';
      const photoRef = ref(storage, `profile-photos/${currentUser.uid}.${fileExtension}`);
      const metadata = {
        contentType: file.type,
        customMetadata: {
          uploadedBy: currentUser.uid,
          uploadedAt: new Date().toISOString()
        }
      };
      await uploadBytes(photoRef, file, metadata);

      // Get download URL
      const downloadURL = await getDownloadURL(photoRef);

      // Update Firebase Auth profile
      await updateProfile(currentUser, {
        photoURL: downloadURL
      });

      // Update Firestore via backend API
      const token = await currentUser.getIdToken(true);
      await axios.post(`${API_BASE_URL}/api/user/profile`, {
        photoURL: downloadURL,
        email: currentUser.email
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 5000
      });

      // Update local profile state
      setProfile(prev => ({ ...prev, photoURL: downloadURL }));

      // Reload user to get updated photoURL
      await currentUser.reload();
      // refresh page
      window.location.reload();
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert(t('profile.failedToUploadPhoto') || 'Failed to upload photo. Please try again.');
    } finally {
      setUploadingPhoto(false);
      // Reset file inputs
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (fileInputRefFreelancer.current) {
        fileInputRefFreelancer.current.value = '';
      }
    }
  }, [currentUser, t]);

  const userRolesString = useMemo(() => {
    return userRoles && userRoles.length > 0 ? userRoles.join(', ') : '';
  }, [userRoles]);

  if (loading) {
    return (
      <div className="profile-container">
        <div className="loading">{t('common.loading')}</div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="profile-container">
        <div className="loading">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className={`profile-container ${currentRole === 'business' ? 'business' : 'freelancer'} ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <header className="profile-header">
        <div className="header-content">
          <div className="page-title-section">
            <div className="page-icon profile-icon">👤</div>
            <h1>{t('profile.title')}</h1>
          </div>
          <div className="user-info">
            <div className="user-details">
              <span className="welcome-line">
                <span className="welcome-label">{t('dashboard.welcome')}</span>{' '}
                <span className="welcome-value">{currentUser.email}</span>
              </span>
              {currentRole && (
                <div className="role-display">
                  <span className="user-role">
                    <span className="role-label">{t('dashboard.activeRole')}</span>{' '}
                    <strong className="role-value">{currentRole}</strong>
                  </span>
                  {/* <button
                    onClick={handleRoleSwitch}
                    className="switch-role-btn"
                  >
                    {t('dashboard.switchRole')}
                  </button> */}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="profile-main">
        <div className="profile-content">
          {currentRole !== 'business' && (
            <FreelancerProfile
              currentUser={currentUser}
              profile={profile}
              isRTL={isRTL}
            />
          )}
          {currentRole !== 'business' && (
            <div className="profile-card freelancer-details-card" style={{color: "black"}} >
              <h2 className="section-title freelancer-details-title">
                {t('profile.freelancerDetails') || 'Freelancer details'}
              </h2>
              <p className="freelancer-details-hint">
                {t('profile.freelancerDetailsHint') || 'Control what businesses see when they view your freelancer profile.'}
              </p>
              {profile && (
                <>
                  <div className="business-type-field">
                    <div className="business-type-display-row">
                      <div>
                        <strong>{t('profile.profilePhoto') || 'Profile Photo'}</strong>
                        <div className="profile-photo-upload-section">
                          <div className="profile-avatar-container">
                            <UserAvatar user={currentUser} size="large" className="profile-avatar-main" />
                            <div className="profile-photo-upload">
                              <input
                                ref={fileInputRefFreelancer}
                                type="file"
                                accept="image/*"
                                onChange={handlePhotoUpload}
                                style={{ display: 'none' }}
                                id="photo-upload-input-freelancer"
                                disabled={uploadingPhoto}
                              />
                              <label htmlFor="photo-upload-input-freelancer" className="photo-upload-label">
                                {uploadingPhoto ? (
                                  <span>{t('profile.uploadingPhoto') || 'Uploading...'}</span>
                                ) : (
                                  <span>{t('profile.uploadPhoto') || 'Upload Photo'}</span>
                                )}
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="business-type-field">
                    <div className="business-type-display-row">
                      <div>
                        <strong>{t('login.displayName')}</strong>
                        {!editingDisplayName ? (
                          <span className="business-type-value">
                            {profile.displayName
                              || currentUser.displayName
                              || (currentUser.email && currentUser.email.split('@')[0])
                              || t('dashboard.noDisplayName')}
                          </span>
                        ) : (
                          <div className="business-type-edit-container">
                            <input
                              id="display-name-edit"
                              type="text"
                              className="business-type-edit-input"
                              placeholder={t('login.businessDisplayNamePlaceholder')}
                              value={displayNameValue}
                              onChange={(e) => setDisplayNameValue(e.target.value)}
                              autoFocus
                            />
                            <div className="business-type-edit-actions">
                              <button
                                className="business-type-save-btn"
                                onClick={async () => {
                                  try {
                                    const token = await currentUser.getIdToken(true);

                                    if (displayNameValue.trim()) {
                                      const checkResponse = await fetch(`${API_BASE_URL}/api/user/check-display-name?displayName=${encodeURIComponent(displayNameValue.trim())}`, {
                                        headers: {
                                          'Authorization': `Bearer ${token}`
                                        }
                                      });

                                      if (checkResponse.ok) {
                                        const checkData = await checkResponse.json();
                                        if (!checkData.available) {
                                          alert(t('login.displayNameTaken'));
                                          return;
                                        }
                                      }
                                    }

                                    const updatePayload = {
                                      displayName: displayNameValue.trim()
                                    };
                                    // Only include email if it exists
                                    if (currentUser.email) {
                                      updatePayload.email = currentUser.email;
                                    }
                                    await axios.post(`${API_BASE_URL}/api/user/profile`, updatePayload, {
                                      headers: {
                                        'Authorization': `Bearer ${token}`,
                                        'Content-Type': 'application/json'
                                      },
                                      timeout: 5000
                                    });
                                    setProfile(prev => ({ ...prev, displayName: displayNameValue.trim() }));
                                    setEditingDisplayName(false);
                                  } catch (error) {
                                    console.error('Error updating display name:', error);
                                    if (error.response?.data?.error?.includes('Display name already taken')) {
                                      alert(t('login.displayNameTaken'));
                                    } else {
                                      alert(t('dashboard.failedToUpdateDisplayName') || 'Failed to update display name. Please try again.');
                                    }
                                  }
                                }}
                              >
                                {t('common.save')}
                              </button>
                              <button
                                className="business-type-cancel-btn"
                                onClick={() => {
                                  setEditingDisplayName(false);
                                  setDisplayNameValue(profile.displayName || '');
                                }}
                              >
                                {t('common.cancel')}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      {!editingDisplayName && (
                        <button
                          className="business-type-edit-btn"
                          onClick={() => {
                            setDisplayNameValue(profile.displayName || '');
                            setEditingDisplayName(true);
                          }}
                        >
                          {t('common.edit')}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="business-type-field">
                    <div className="business-type-display-row">
                      <div>
                        <strong>{t('jobs.location')}</strong>
                        {!editingLocation ? (
                          <span className="business-type-value">
                            {profile.location || '—'}
                          </span>
                        ) : (
                          <div className="business-type-edit-container">
                            <CityAutocomplete
                              inputId="freelancer-location-edit"
                              value={locationValue}
                              onChange={setLocationValue}
                              onValidityChange={setLocationValid}
                              enforceSelection
                              placeholder={t('profile.locationPlaceholder') || 'Type your city (Hebrew or English)'}
                              required
                            />
                            <div className="business-type-edit-actions">
                              <button
                                className="business-type-save-btn"
                                onClick={async () => {
                                  try {
                                    if (!locationValue.trim() || !locationValid) {
                                      alert(t('profile.locationRequired') || 'Location is required');
                                      return;
                                    }
                                    const token = await currentUser.getIdToken(true);
                                    await axios.post(`${API_BASE_URL}/api/user/profile`, {
                                      location: locationValue.trim(),
                                      email: currentUser.email
                                    }, {
                                      headers: {
                                        'Authorization': `Bearer ${token}`,
                                        'Content-Type': 'application/json'
                                      },
                                      timeout: 5000
                                    });
                                    setProfile(prev => ({ ...prev, location: locationValue.trim() }));
                                    setEditingLocation(false);
                                  } catch (error) {
                                    alert('Failed to update location. Please try again.');
                                  }
                                }}
                              >
                                {t('common.save')}
                              </button>
                              <button
                                className="business-type-cancel-btn"
                                onClick={() => {
                                  setEditingLocation(false);
                                  setLocationValue(profile.location || '');
                                  setLocationValid(!!profile.location);
                                }}
                              >
                                {t('common.cancel')}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      {!editingLocation && (
                        <button
                          className="business-type-edit-btn"
                          onClick={() => {
                            setLocationValue(profile.location || '');
                            setLocationValid(!!profile.location);
                            setEditingLocation(true);
                          }}
                        >
                          {t('common.edit')}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="business-type-field">
                    <div className="business-type-display-row">
                      <div>
                        <strong>{t('profile.about') || 'About'}</strong>
                        {!editingAbout ? (
                          <span className="business-type-value">
                            {profile.about || t('profile.noAbout') || 'Add a short description so businesses know more about you.'}
                          </span>
                        ) : (
                          <div className="business-type-edit-container">
                            <textarea
                              id="freelancer-about-edit"
                              className="business-type-edit-input"
                              placeholder={t('profile.aboutPlaceholder') || 'Describe your experience, skills, and what you offer.'}
                              value={aboutValue}
                              onChange={(e) => setAboutValue(e.target.value)}
                              rows="4"
                              style={{ resize: 'vertical' }}
                              autoFocus
                            />
                            <div className="business-type-edit-actions">
                              <button
                                className="business-type-save-btn"
                                onClick={async () => {
                                  try {
                                    const token = await currentUser.getIdToken(true);
                                    await axios.post(`${API_BASE_URL}/api/user/profile`, {
                                      about: aboutValue.trim() || null,
                                      email: currentUser.email
                                    }, {
                                      headers: {
                                        'Authorization': `Bearer ${token}`,
                                        'Content-Type': 'application/json'
                                      },
                                      timeout: 5000
                                    });
                                    setProfile(prev => ({ ...prev, about: aboutValue.trim() || null }));
                                    setEditingAbout(false);
                                  } catch (error) {
                                    console.error('Error updating about:', error);
                                    alert(t('profile.failedToUpdateAbout') || 'Failed to update about section. Please try again.');
                                  }
                                }}
                              >
                                {t('common.save')}
                              </button>
                              <button
                                className="business-type-cancel-btn"
                                onClick={() => {
                                  setEditingAbout(false);
                                  setAboutValue(profile.about || '');
                                }}
                              >
                                {t('common.cancel')}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      {!editingAbout && (
                        <button
                          className="business-type-edit-btn"
                          onClick={() => {
                            setAboutValue(profile.about || '');
                            setEditingAbout(true);
                          }}
                        >
                          {t('common.edit')}
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          {currentRole !== 'business' && (
            <div className="profile-card" style={{ marginTop: '1rem' }}>
              <h2 className="section-title" style={{ marginBottom: '1rem' }}>{t('dashboard.visibilityAndAvailability') || 'Visibility & Availability'}</h2>
              <div className="visibility-toggle">
                <label className="visibility-label">
                  <input
                    type="checkbox"
                    checked={profile?.visible || false}
                    onChange={(e) => {
                      if (e.target.checked) {
                        e.preventDefault();
                        setShowAvailabilityForm(true);
                      } else {
                        updateVisibility(false);
                      }
                    }}
                    disabled={updatingVisibility || showAvailabilityForm}
                    className="visibility-checkbox"
                  />
                  <span className="visibility-text">
                    {profile?.visible ? t('dashboard.visible') : t('dashboard.notVisible')}
                  </span>
                </label>
                <p className="visibility-hint">{t('dashboard.freelancerVisibilityHint')}</p>

                {showAvailabilityForm && (
                  <div className="visibility-form-container">
                    <h4>{t('dashboard.availabilityFormTitle')}</h4>
                    <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '1rem', fontSize: '0.9rem' }}>
                      {t('dashboard.availabilityFormDescription')}
                    </p>
                    <div style={{ marginBottom: '1rem' }}>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingRangeIndex(null);
                          setShowAvailabilityPicker(true);
                        }}
                        style={{
                          padding: '0.75rem 1.5rem',
                          background: 'rgba(59, 130, 246, 0.2)',
                          border: '1px solid rgba(59, 130, 246, 0.4)',
                          borderRadius: '6px',
                          color: 'rgba(255, 255, 255, 0.9)',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '500'
                        }}
                      >
                        {t('dashboard.addAvailabilityRange') || '+ Add Availability Range'}
                      </button>
                    </div>
                    {availabilityRanges.length === 0 ? (
                      <p style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9rem' }}>
                        {t('dashboard.noAvailabilityRanges') || 'No availability ranges set. Click the button above to add one.'}
                      </p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1rem' }}>
                        {availabilityRanges.map((range, index) => (
                          <div
                            key={index}
                            style={{
                              padding: '1rem',
                              background: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: '8px',
                              border: '1px solid rgba(255, 255, 255, 0.1)'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: '500', marginBottom: '0.25rem' }}>
                                  {new Date(range.startDate).toLocaleDateString()} - {new Date(range.endDate).toLocaleDateString()}
                                </div>
                                <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }}>
                                  {range.startTime} - {range.endTime}
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button
                                  type="button"
                                  onClick={() => editAvailabilityRange(index)}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    background: 'rgba(59, 130, 246, 0.2)',
                                    border: '1px solid rgba(59, 130, 246, 0.4)',
                                    borderRadius: '6px',
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem'
                                  }}
                                >
                                  {t('common.edit') || 'Edit'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeAvailabilityRange(index)}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    background: 'rgba(239, 68, 68, 0.2)',
                                    border: '1px solid rgba(239, 68, 68, 0.4)',
                                    borderRadius: '6px',
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem'
                                  }}
                                >
                                  {t('common.delete') || 'Delete'}
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="visibility-form-actions">
                      <button
                        className="visibility-form-save-btn"
                        onClick={() => {
                          // Validate at least one valid availability range
                          const hasValidAvailability = availabilityRanges.some((range) => {
                            if (!range.startDate || !range.endDate || !range.startTime || !range.endTime) return false;
                            if (!validateTime(range.startTime) || !validateTime(range.endTime)) return false;
                            if (range.endDate < range.startDate) return false;
                            if (range.endDate === range.startDate && range.endTime <= range.startTime) return false;
                            return true;
                          });

                          if (!hasValidAvailability) {
                            alert(t('dashboard.availabilityRequired') || 'Please add at least one availability range');
                            return;
                          }

                          updateVisibility(true, null, null, null, null, null, null, null, availabilityRanges);
                        }}
                        disabled={updatingVisibility}
                      >
                        {updatingVisibility ? t('common.saving') : t('common.save')}
                      </button>
                      <button
                        className="visibility-form-cancel-btn"
                        onClick={() => {
                          setShowAvailabilityForm(false);
                          // Reset to profile availability if exists
                          if (profile?.availabilityRanges) {
                            setAvailabilityRanges(profile.availabilityRanges);
                          } else {
                            setAvailabilityRanges([]);
                          }
                        }}
                        disabled={updatingVisibility}
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                )}

                {profile?.visible && currentRole !== 'business' && profile?.availabilityRanges && (
                  <div className="business-visibility-info" style={{ marginTop: '1rem' }}>
                    <h4 style={{ color: 'rgba(255, 255, 255, 0.9)', marginBottom: '0.75rem', fontSize: '1rem' }}>
                      {t('dashboard.currentAvailability')}
                    </h4>
                    {profile.availabilityRanges && profile.availabilityRanges.length > 0 ? (
                      profile.availabilityRanges.map((range, index) => (
                        <p key={index} style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                          <strong>
                            {new Date(range.startDate).toLocaleDateString()} - {new Date(range.endDate).toLocaleDateString()}:
                          </strong>{' '}
                          {range.startTime} - {range.endTime}
                        </p>
                      ))
                    ) : (
                      <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }}>
                        {t('dashboard.noAvailabilitySet')}
                      </p>
                    )}
                    <button
                      className="business-type-edit-btn"
                      onClick={() => {
                        setShowAvailabilityForm(true);
                      }}
                      style={{ marginTop: '0.5rem' }}
                    >
                      {t('dashboard.editAvailability')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          {currentUser && currentUser.providerData && currentUser.providerData.length > 0 &&
           (currentUser.providerData[0].providerId === 'password' ||
            (currentUser.providerData.some(p => p.providerId === 'google.com') &&
             currentUser.providerData.some(p => p.providerId === 'password'))) && (
            <div className="profile-card" style={{ marginTop: '1rem' }}>
              <div className="change-password-section">
                <h3 className="section-title" style={{ marginBottom: '1rem' }}>
                  {t('profile.changePassword') || 'Change Password'}
                </h3>
                <p style={{ marginBottom: '1rem', color: '#666' }}>
                  {t('profile.changePasswordDescription') || 'Change your password by entering your current password and a new password.'}
                </p>
                {!showChangePasswordForm ? (
                  <button
                    onClick={() => setShowChangePasswordForm(true)}
                    className="change-password-btn"
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#2563EB',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '1rem'
                    }}
                  >
                    {t('profile.changePassword') || 'Change Password'}
                  </button>
                ) : (
                  <div className="change-password-form">
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                        {t('profile.oldPassword') || 'Current Password'}
                      </label>
                      <input
                        type="password"
                        value={oldPassword}
                        onChange={(e) => {
                          setOldPassword(e.target.value);
                          setChangePasswordError('');
                        }}
                        placeholder={t('profile.oldPasswordPlaceholder') || 'Enter current password'}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: changePasswordError ? '2px solid #f44336' : '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '1rem'
                        }}
                        disabled={changingPassword}
                      />
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                        {t('login.password') || 'New Password'}
                      </label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          setChangePasswordError('');
                        }}
                        placeholder={t('login.passwordPlaceholder') || 'Enter new password (min 6 characters)'}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: changePasswordError ? '2px solid #f44336' : '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '1rem'
                        }}
                        disabled={changingPassword}
                      />
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                        {t('login.confirmPassword') || 'Confirm New Password'}
                      </label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          setChangePasswordError('');
                        }}
                        placeholder={t('login.confirmPasswordPlaceholder') || 'Confirm new password'}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: changePasswordError ? '2px solid #f44336' : '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '1rem'
                        }}
                        disabled={changingPassword}
                      />
                    </div>
                    {changePasswordError && (
                      <div style={{ color: '#f44336', marginBottom: '1rem', fontSize: '0.9rem' }}>
                        {changePasswordError}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={handleChangePassword}
                        disabled={changingPassword}
                        style={{
                          padding: '0.75rem 1.5rem',
                          backgroundColor: '#2563EB',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: changingPassword ? 'not-allowed' : 'pointer',
                          fontSize: '1rem',
                          opacity: changingPassword ? 0.6 : 1
                        }}
                      >
                        {changingPassword ? (t('profile.changingPassword') || 'Changing...') : (t('profile.changePassword') || 'Change Password')}
                      </button>
                      <button
                        onClick={() => {
                          setShowChangePasswordForm(false);
                          setOldPassword('');
                          setNewPassword('');
                          setConfirmPassword('');
                          setChangePasswordError('');
                        }}
                        disabled={changingPassword}
                        style={{
                          padding: '0.75rem 1.5rem',
                          backgroundColor: '#ccc',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: changingPassword ? 'not-allowed' : 'pointer',
                          fontSize: '1rem'
                        }}
                      >
                        {t('common.cancel') || 'Cancel'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="profile-card" style={{ marginTop: '1rem' }}>
            <div className="password-recovery-section">
              <h3 className="section-title" style={{ marginBottom: '1rem' }}>
                {t('profile.passwordRecovery') || 'Password Recovery'}
              </h3>
              <p style={{ marginBottom: '1rem', color: '#666' }}>
                {t('profile.passwordRecoveryDescription') || 'Forgot your password? Enter your email to receive a 6-digit code to reset it.'}
              </p>
              {!showPasswordRecoveryForm ? (
                <button
                  onClick={() => {
                    setShowPasswordRecoveryForm(true);
                    setRecoveryEmail(currentUser?.email || '');
                  }}
                  className="password-recovery-btn"
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: 'red',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '1rem'
                  }}
                >
                  {t('profile.resetPassword') || 'Reset Password'}
                </button>
              ) : (
                <div className="password-recovery-form">
                  {!recoveryCodeSent ? (
                    <>
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                          {t('login.email') || 'Email'}
                        </label>
                        <input
                          type="email"
                          value={recoveryEmail}
                          onChange={(e) => {
                            setRecoveryEmail(e.target.value);
                            setRecoveryError('');
                          }}
                          placeholder={t('login.emailPlaceholder') || 'Enter your email'}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: recoveryError ? '2px solid #f44336' : '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '1rem'
                          }}
                          disabled={requestingRecoveryCode}
                        />
                      </div>
                      {recoveryError && (
                        <div style={{ color: '#f44336', marginBottom: '1rem', fontSize: '0.9rem' }}>
                          {recoveryError}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={handleRequestPasswordRecoveryCode}
                          disabled={requestingRecoveryCode || !recoveryEmail.trim()}
                          style={{
                            padding: '0.75rem 1.5rem',
                            backgroundColor: 'red',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: (requestingRecoveryCode || !recoveryEmail.trim()) ? 'not-allowed' : 'pointer',
                            fontSize: '1rem',
                            opacity: (requestingRecoveryCode || !recoveryEmail.trim()) ? 0.6 : 1
                          }}
                        >
                          {requestingRecoveryCode ? (t('profile.sendingCode') || 'Sending...') : (t('profile.sendRecoveryCode') || 'Send Recovery Code')}
                        </button>
                        <button
                          onClick={() => {
                            setShowPasswordRecoveryForm(false);
                            setRecoveryEmail('');
                            setRecoveryCode('');
                            setRecoveryNewPassword('');
                            setRecoveryConfirmPassword('');
                            setRecoveryError('');
                            setRecoveryCodeSent(false);
                          }}
                          disabled={requestingRecoveryCode}
                          style={{
                            padding: '0.75rem 1.5rem',
                            backgroundColor: '#ccc',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: requestingRecoveryCode ? 'not-allowed' : 'pointer',
                            fontSize: '1rem'
                          }}
                        >
                          {t('common.cancel') || 'Cancel'}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                          {t('profile.recoveryCode') || 'Recovery Code'}
                        </label>
                        <input
                          type="text"
                          value={recoveryCode}
                          onChange={(e) => {
                            setRecoveryCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                            setRecoveryError('');
                          }}
                          placeholder="123456"
                          maxLength={6}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: recoveryError ? '2px solid #f44336' : '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '1rem',
                            letterSpacing: '0.3em',
                            textAlign: 'center'
                          }}
                          disabled={resettingPassword}
                        />
                        <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                          {t('profile.enterRecoveryCode') || 'Enter the 6-digit code sent to your email'}
                        </p>
                      </div>
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                          {t('login.password') || 'New Password'}
                        </label>
                        <input
                          type="password"
                          value={recoveryNewPassword}
                          onChange={(e) => {
                            setRecoveryNewPassword(e.target.value);
                            setRecoveryError('');
                          }}
                          placeholder={t('login.passwordPlaceholder') || 'Enter new password (min 6 characters)'}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: recoveryError ? '2px solid #f44336' : '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '1rem'
                          }}
                          disabled={resettingPassword}
                        />
                      </div>
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                          {t('login.confirmPassword') || 'Confirm New Password'}
                        </label>
                        <input
                          type="password"
                          value={recoveryConfirmPassword}
                          onChange={(e) => {
                            setRecoveryConfirmPassword(e.target.value);
                            setRecoveryError('');
                          }}
                          placeholder={t('login.confirmPasswordPlaceholder') || 'Confirm new password'}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: recoveryError ? '2px solid #f44336' : '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '1rem'
                          }}
                          disabled={resettingPassword}
                        />
                      </div>
                      {recoveryError && (
                        <div style={{ color: '#f44336', marginBottom: '1rem', fontSize: '0.9rem' }}>
                          {recoveryError}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={handleResetPassword}
                          disabled={resettingPassword || recoveryCode.length !== 6 || !recoveryNewPassword.trim() || recoveryNewPassword !== recoveryConfirmPassword}
                          style={{
                            padding: '0.75rem 1.5rem',
                            backgroundColor: '#f59e0b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: (resettingPassword || recoveryCode.length !== 6 || !recoveryNewPassword.trim() || recoveryNewPassword !== recoveryConfirmPassword) ? 'not-allowed' : 'pointer',
                            fontSize: '1rem',
                            opacity: (resettingPassword || recoveryCode.length !== 6 || !recoveryNewPassword.trim() || recoveryNewPassword !== recoveryConfirmPassword) ? 0.6 : 1
                          }}
                        >
                          {resettingPassword ? (t('profile.resettingPassword') || 'Resetting...') : (t('profile.resetPassword') || 'Reset Password')}
                        </button>
                        <button
                          onClick={() => {
                            setShowPasswordRecoveryForm(false);
                            setRecoveryEmail('');
                            setRecoveryCode('');
                            setRecoveryNewPassword('');
                            setRecoveryConfirmPassword('');
                            setRecoveryError('');
                            setRecoveryCodeSent(false);
                          }}
                          disabled={resettingPassword}
                          style={{
                            padding: '0.75rem 1.5rem',
                            backgroundColor: '#ccc',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: resettingPassword ? 'not-allowed' : 'pointer',
                            fontSize: '1rem'
                          }}
                        >
                          {t('common.cancel') || 'Cancel'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          {currentRole !== 'business' && (
            <div className="profile-card" style={{ marginTop: '1rem' }}>
              <div className="delete-account-section">
                <button
                  onClick={handleDeleteAccount}
                  className="delete-account-btn"
                  disabled={deletingAccount}
                >
                  {deletingAccount ? t('login.deletingAccount') : t('login.deleteAccount')}
                </button>
              </div>
            </div>
          )}
          {currentRole === 'business' && (
          <div className="profile-card">
            <div className="profile-header-section">
              <div className="profile-avatar-container">
                <UserAvatar user={currentUser} size="large" className="profile-avatar-main" />
                <div className="profile-photo-upload">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    style={{ display: 'none' }}
                    id="photo-upload-input-business"
                    disabled={uploadingPhoto}
                  />
                  <label htmlFor="photo-upload-input-business" className="photo-upload-label">
                    {uploadingPhoto ? (
                      <span>{t('profile.uploadingPhoto') || 'Uploading...'}</span>
                    ) : (
                      <span>{t('profile.uploadPhoto') || 'Upload Photo'}</span>
                    )}
                  </label>
                </div>
              </div>
              <div className="profile-header-info">
                <h2 className="section-title">{t('dashboard.userProfile')}</h2>
                <p className="profile-email">{currentUser.email}</p>
              </div>
            </div>
            <div className="profile-info">
              {currentRole && <p><strong>{t('dashboard.activeRole')}</strong> {currentRole}</p>}
              <p><strong>{t('dashboard.uid')}</strong> {currentUser.uid}</p>
              <p><strong>{t('dashboard.emailVerified')}</strong> {currentUser.emailVerified ? t('common.yes') : t('common.no')}</p>
              {profile && (
                <>
                  {currentRole === 'business' && (
                    <>
                      <div className="business-type-field">
                        <div className="business-type-display-row">
                          <div>
                            <strong>{t('dashboard.displayName')}</strong>
                            {!editingDisplayName ? (
                              <span className="business-type-value">
                                {profile.displayName || t('dashboard.noDisplayName')}
                              </span>
                            ) : (
                            <div className="business-type-edit-container">
                              <input
                                id="display-name-edit"
                                type="text"
                                className="business-type-edit-input"
                                placeholder={t('login.businessDisplayNamePlaceholder')}
                                value={displayNameValue}
                                onChange={(e) => setDisplayNameValue(e.target.value)}
                                autoFocus
                              />
                              <div className="business-type-edit-actions">
                                <button
                                  className="business-type-save-btn"
                                  onClick={async () => {
                                    try {
                                      if (!displayNameValue.trim()) {
                                        alert(t('login.businessDisplayNameRequired'));
                                        return;
                                      }

                                      const token = await currentUser.getIdToken(true);
                                      const checkResponse = await fetch(`${API_BASE_URL}/api/user/check-display-name?displayName=${encodeURIComponent(displayNameValue.trim())}`, {
                                        headers: {
                                          'Authorization': `Bearer ${token}`
                                        }
                                      });

                                      if (checkResponse.ok) {
                                        const checkData = await checkResponse.json();
                                        if (!checkData.available) {
                                          alert(t('login.displayNameTaken'));
                                          return;
                                        }
                                      }

                                      const updatePayload = {
                                        displayName: displayNameValue.trim()
                                      };
                                      // Only include email if it exists
                                      if (currentUser.email) {
                                        updatePayload.email = currentUser.email;
                                      }
                                      await axios.post(`${API_BASE_URL}/api/user/profile`, updatePayload, {
                                        headers: {
                                          'Authorization': `Bearer ${token}`,
                                          'Content-Type': 'application/json'
                                        },
                                        timeout: 5000
                                      });
                                      setProfile(prev => ({ ...prev, displayName: displayNameValue.trim() }));
                                      setEditingDisplayName(false);
                                    } catch (error) {
                                      // Error updating display name
                                      if (error.response?.data?.error?.includes('Display name already taken')) {
                                        alert(t('login.displayNameTaken'));
                                      } else if (error.response?.data?.error?.includes('Display name is required')) {
                                        alert(t('login.businessDisplayNameRequired'));
                                      } else {
                                        alert(t('dashboard.failedToUpdateDisplayName') || 'Failed to update display name. Please try again.');
                                      }
                                    }
                                  }}
                                >
                                  {t('common.save')}
                                </button>
                                <button
                                  className="business-type-cancel-btn"
                                  onClick={() => {
                                    setEditingDisplayName(false);
                                    setDisplayNameValue(profile.displayName || '');
                                  }}
                                >
                                  {t('common.cancel')}
                                </button>
                              </div>
                            </div>
                          )}
                          </div>
                          {!editingDisplayName && (
                            <button
                              className="business-type-edit-btn"
                              onClick={() => {
                                setDisplayNameValue(profile.displayName || '');
                                setEditingDisplayName(true);
                              }}
                            >
                              {t('common.edit')}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="business-type-field">
                        <div className="business-type-display-row">
                          <div>
                            <strong>{t('dashboard.businessType')}</strong>
                            {!editingBusinessType ? (
                              <span className="business-type-value">
                                {profile.businessType || t('dashboard.noBusinessType')}
                              </span>
                            ) : (
                            <div className="business-type-edit-container">
                              <input
                                id="business-type-edit"
                                type="text"
                                className="business-type-edit-input"
                                placeholder={t('dashboard.businessTypePlaceholder')}
                                value={businessTypeValue}
                                onChange={(e) => setBusinessTypeValue(e.target.value)}
                                autoFocus
                              />
                              <div className="business-type-edit-actions">
                                <button
                                  className="business-type-save-btn"
                                  onClick={async () => {
                                    try {
                                      const token = await currentUser.getIdToken(true);
                                      await axios.post(`${API_BASE_URL}/api/user/profile`, {
                                        businessType: businessTypeValue.trim(),
                                        email: currentUser.email
                                      }, {
                                        headers: {
                                          'Authorization': `Bearer ${token}`,
                                          'Content-Type': 'application/json'
                                        },
                                        timeout: 5000
                                      });
                                      setProfile(prev => ({ ...prev, businessType: businessTypeValue.trim() }));
                                      setEditingBusinessType(false);
                                    } catch (error) {
                                      // Error updating business type
                                      alert(t('dashboard.failedToUpdateBusinessType'));
                                    }
                                  }}
                                >
                                  {t('common.save')}
                                </button>
                                <button
                                  className="business-type-cancel-btn"
                                  onClick={() => {
                                    setEditingBusinessType(false);
                                    setBusinessTypeValue(profile.businessType || '');
                                  }}
                                >
                                  {t('common.cancel')}
                                </button>
                              </div>
                            </div>
                          )}
                          </div>
                          {!editingBusinessType && (
                            <button
                              className="business-type-edit-btn"
                              onClick={() => {
                                setBusinessTypeValue(profile.businessType || '');
                                setEditingBusinessType(true);
                              }}
                            >
                              {t('common.edit')}
                            </button>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                  {profile.favoriteColor && <p><strong>{t('dashboard.favoriteColor')}</strong> {profile.favoriteColor}</p>}
                </>
              )}
              {currentRole !== 'business' && currentRole !== 'employer' && (
                <div className="visibility-toggle">
                  <label className="visibility-label">
                    <input
                      type="checkbox"
                      checked={profile?.visible || false}
                      onChange={(e) => {
                        updateVisibility(e.target.checked);
                      }}
                      disabled={updatingVisibility}
                      className="visibility-checkbox"
                    />
                    <span className="visibility-text">
                      {profile?.visible ? t('dashboard.visible') : t('dashboard.notVisible')}
                    </span>
                  </label>
                  <p className="visibility-hint">{t('dashboard.freelancerVisibilityHint')}</p>
                </div>
              )}
              <div className="delete-account-section" style={{ marginTop: '2rem' }}>
                <button
                  onClick={handleDeleteAccount}
                  className="delete-account-btn"
                  disabled={deletingAccount}
                >
                  {deletingAccount ? t('login.deletingAccount') : t('login.deleteAccount')}
                </button>
              </div>
            </div>
          </div>
          )}
        </div>
      </main>
      {showAvailabilityPicker && (
        <TimeDatePickerModal
          isOpen={showAvailabilityPicker}
          onClose={() => {
            setShowAvailabilityPicker(false);
            setEditingRangeIndex(null);
          }}
          onConfirm={(result) => {
            if (result.date && result.endDate && result.time && result.endTime) {
              addAvailabilityRange({
                startDate: result.date,
                endDate: result.endDate,
                startTime: result.time,
                endTime: result.endTime
              });
            }
          }}
          mode="datetimerange"
          initialDate={editingRangeIndex !== null ? availabilityRanges[editingRangeIndex]?.startDate : null}
          initialEndDate={editingRangeIndex !== null ? availabilityRanges[editingRangeIndex]?.endDate : null}
          initialTime={editingRangeIndex !== null ? availabilityRanges[editingRangeIndex]?.startTime : null}
          initialEndTime={editingRangeIndex !== null ? availabilityRanges[editingRangeIndex]?.endTime : null}
          title={t('dashboard.selectAvailabilityRange') || 'Select Date Range & Times'}
        />
      )}
    </div>
  );
}



